import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, } from '@cargomatic/ui';
import { isEmpty, uniqBy, isString } from 'lodash';
import { humanize, unescapeHTML } from 'underscore.string';
import Pagination from 'components/v2/PaginationV2';
import Spinner from 'components/ui/Spinner';
import { sluggify } from 'utils/localization/utils';
import { CARRIER_SUBSIDIARY_OPTIONS } from 'constants/subsidiary';
import { DRIVER_CATEGORIZATION_OPTIONS } from 'constants/DriverCategorization';
function getEventSource(source) {
    if (source.email === 'cargomatic-automation' ||
        source.email === 'cargomatic-automation@cargomatic.com') {
        return (React.createElement("img", { src: "/img/storm-trooper.jpg", alt: "cargomatic-automation", className: "icon", width: "25" }));
    }
    if (source._type === 'Driver') {
        if (!source.first_name)
            return source.email;
        if (source.first_name && source.first_name !== '') {
            return `${source.username} (${source.first_name} ${source.last_name})`;
        }
        return source.username;
    }
    return source.email;
}
const AuditTextToTranslate = [
    [...CARRIER_SUBSIDIARY_OPTIONS, ...DRIVER_CATEGORIZATION_OPTIONS].map((o) => o.value),
].flat();
function getEventLabel(event, t) {
    const eventKey = sluggify(event);
    let label = t(eventKey);
    if (!label || label === eventKey) {
        label = humanize(event);
    }
    return label;
}
function getDate(date) {
    return moment(date).format('L');
}
function renderNameCell(item, t) {
    var _a, _b, _c, _d, _e;
    const event = getEventLabel(item.event, t);
    if ((_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a.referenceNumberType) {
        return (React.createElement(TableCell, { className: "whitespace-nowrap" },
            React.createElement("div", { className: "text-sm", title: event },
                event,
                React.createElement("div", { style: { marginTop: 16 } },
                    React.createElement("div", null,
                        t('REFERENCE_NUMBER_TYPE'),
                        ":",
                        ' ',
                        t(sluggify((_b = item === null || item === void 0 ? void 0 : item.meta) === null || _b === void 0 ? void 0 : _b.referenceNumberType))),
                    ['before', 'after'].map((el) => {
                        var _a;
                        if (!(el in item.meta))
                            return null;
                        return (React.createElement("div", { style: { marginTop: 4 }, key: el },
                            t(el.toUpperCase()),
                            ":",
                            ' ',
                            isString((_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a[el])
                                ? t(sluggify(item.meta[el]))
                                : 'null'));
                    })))));
    }
    let change = '';
    let link = '';
    let description = null;
    if (item.meta && item.meta.event_description) {
        description = item.meta.event_description;
    }
    if (item.meta && item.meta.value) {
        const value = item.meta.value;
        let shipment = '';
        let order = '';
        if (item.meta.shipment && typeof item.meta.shipment === 'string') {
            shipment = item.meta.shipment;
        }
        if (item.meta.order && typeof item.meta.order === 'string') {
            order = item.meta.order;
        }
        if (typeof value === 'string') {
            if (!isEmpty(shipment) || !isEmpty(order)) {
                const valueSplit = value.split(' ');
                const linkId = valueSplit[valueSplit.length - 1];
                valueSplit.pop();
                const path = !isEmpty(shipment) ? 'shipments' : 'orders';
                const id = !isEmpty(shipment) ? shipment : order;
                change = valueSplit.join(' ');
                link = (React.createElement("a", { href: `/${path}/${id}`, target: "_blank", rel: "noreferrer noopener" }, linkId));
            }
            else {
                change = unescapeHTML(unescape(item.meta.value));
            }
        }
    }
    let text;
    let title;
    if (change.length > 50) {
        text = change.substring(0, 40) + '...[truncated]';
        title = change;
    }
    else if (change && link) {
        text = `{change} {link}`;
        title = text;
    }
    else {
        text = change;
        title = change;
    }
    if (((_c = item === null || item === void 0 ? void 0 : item.meta) === null || _c === void 0 ? void 0 : _c.contractId) && ((_d = item === null || item === void 0 ? void 0 : item.meta) === null || _d === void 0 ? void 0 : _d.to)) {
        const { start_date: toStartDate = '', end_date: toEndDate = '' } = item.meta.to;
        if ((_e = item === null || item === void 0 ? void 0 : item.meta) === null || _e === void 0 ? void 0 : _e.from) {
            const { start_date: fromStartDate = '', end_date: fromEndDate = '' } = item.meta.from;
            change = t('START_DATE_END_DATE_CHANGED', {
                startFrom: getDate(fromStartDate),
                startTo: getDate(toStartDate),
                endFrom: getDate(fromEndDate),
                endTo: getDate(toEndDate),
            });
        }
        if (!change) {
            change = t('START_DATE_END_DATE_TIMELINE', {
                startDate: getDate(toStartDate),
                endDate: getDate(toEndDate),
            });
        }
        text = change;
    }
    return (React.createElement(TableCell, { className: "whitespace-nowrap" },
        React.createElement("div", { className: "text-sm", title: event }, event),
        !isEmpty(change) && (React.createElement("div", { style: { paddingTop: 20 }, className: "text-sm text-grey-dark", title: title }, t('CHANGE_WITH_VALUE', {
            text: AuditTextToTranslate.includes(text)
                ? t(sluggify(text))
                : text,
        }))),
        description && (React.createElement("div", { style: { paddingTop: 20 }, className: "text-sm text-grey-dark", title: description }, description))));
}
function AuditHistory({ audits: auditsProp, paginate = true, keepDuplicates, itemsPerPage = 10, onClick, isLoading, currentPage, }) {
    const { t } = useTranslation();
    const [activePage, setActivePage] = useState(1);
    const { processedAudits, totalCount } = useMemo(() => {
        if (!auditsProp)
            return { processedAudits: [], totalCount: 0 };
        let items;
        let total;
        if (Array.isArray(auditsProp)) {
            items = auditsProp;
            total = items.length;
        }
        else if (auditsProp && auditsProp.items) {
            items = auditsProp.items;
            total = auditsProp.totalCount;
        }
        else {
            return { processedAudits: [], totalCount: 0 };
        }
        if (!keepDuplicates) {
            items = uniqBy(items, ({ meta, event }) => JSON.stringify({ meta, event }));
            total = items.length;
        }
        return { processedAudits: items, totalCount: total };
    }, [auditsProp, keepDuplicates]);
    // Clamp page when data shrinks
    useEffect(() => {
        if (currentPage != null)
            return;
        const totalPages = Math.max(Math.ceil(processedAudits.length / itemsPerPage), 1);
        if (activePage > totalPages) {
            setActivePage(totalPages);
        }
    }, [processedAudits.length, itemsPerPage, activePage, currentPage]);
    const page = currentPage || activePage;
    const activeItems = useMemo(() => {
        if (!paginate)
            return processedAudits;
        return processedAudits.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    }, [processedAudits, paginate, page, itemsPerPage]);
    if (!auditsProp || processedAudits.length === 0) {
        return React.createElement("div", null,
            t('AWAITING_AUDIT_EVENTS'),
            "..");
    }
    const onPageClick = onClick || ((p) => setActivePage(p));
    return (React.createElement("div", { className: "overflow-x-auto" },
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, t('NAME')),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, t('TIMESTAMP')),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, t('SOURCE')))),
            React.createElement(TableBody, null, activeItems.map((item, index) => {
                const timestamp = moment(item.timestamp).format('LLL');
                const source = getEventSource(item.user);
                return (React.createElement(TableRow, { key: index },
                    renderNameCell(item, t),
                    React.createElement(TableCell, { className: "whitespace-nowrap" },
                        React.createElement("div", { className: "text-sm", title: timestamp }, timestamp)),
                    React.createElement(TableCell, { className: "whitespace-nowrap" },
                        React.createElement("div", { className: "text-sm", title: source }, source))));
            }))),
        paginate &&
            (isLoading ? (React.createElement("div", { className: "flex justify-center my-2" },
                React.createElement(Spinner, { message: t('LOADING') }))) : (React.createElement(Pagination, { perPage: itemsPerPage, page: page, total: totalCount, onPageClick: onPageClick, showCount: false })))));
}
AuditHistory.propTypes = {
    audits: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
            meta: PropTypes.shape({
                value: PropTypes.any,
            }),
            event: PropTypes.string,
        })),
        PropTypes.shape({
            items: PropTypes.array,
            totalCount: PropTypes.number,
        }),
    ]),
    paginate: PropTypes.bool,
    keepDuplicates: PropTypes.bool,
    itemsPerPage: PropTypes.number,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    currentPage: PropTypes.number,
};
export default AuditHistory;
